// Login state lives in localStorage, but the JWT is the only part of it that
// actually expires. Reading `isAuthenticated` on its own leaves the UI convinced
// it is signed in with a dead token, so every check goes through here instead.

import { UserInfo } from '../api/auth';

const SESSION_KEYS = ['isAuthenticated', 'username', 'authToken', 'userInfo'];

export function clearSession(): void {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}

// Reads `exp` out of the JWT payload. The payload is base64url, which `atob` does
// not accept as-is, so translate the alphabet and restore the padding first.
// Anything we cannot parse counts as expired.
export function isTokenValid(token: string | null): boolean {
  if (!token) return false;

  try {
    const payload = token.split('.')[1];
    if (!payload) return false;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = (4 - (base64.length % 4)) % 4;
    const { exp } = JSON.parse(atob(base64 + '='.repeat(padding)));

    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch (error) {
    return false;
  }
}

// Returns the stored user, or null if there is no usable session. Clears the
// leftovers on the way out so nothing downstream sees a half-valid session.
export function loadSession(): UserInfo | null {
  const storedUserInfo = localStorage.getItem('userInfo');

  if (
    localStorage.getItem('isAuthenticated') !== 'true' ||
    !storedUserInfo ||
    !isTokenValid(localStorage.getItem('authToken'))
  ) {
    clearSession();
    return null;
  }

  try {
    return JSON.parse(storedUserInfo);
  } catch (error) {
    console.error('Error parsing stored user info:', error);
    clearSession();
    return null;
  }
}
